// src/components/ReviewSlider.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Star, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';

const ReviewSlider = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const q = query(
          collection(db, 'reviews'),
          where('approved', '==', true)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setReviews(data.slice(0, 10)); // Max 10 reviews
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Auto slide
  useEffect(() => {
    if (reviews.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  const nextReview = () => setCurrentIndex(prev => (prev + 1) % reviews.length);
  const prevReview = () => setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length);

  if (loading) {
    return (
      <section className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        <div className="h-64 bg-gray-200 rounded-3xl animate-pulse"></div>
      </section>
    );
  }

  if (reviews.length === 0) return null;

  const review = reviews[currentIndex];

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 anim-fadeInUp">

      {/* Header */}
      <div className="text-center mb-6 md:mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
          style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)', color: 'var(--color-link)' }}>
          <MessageCircle size={14} />
          <span className="text-xs font-bold uppercase tracking-wider">Testimonials</span>
        </div>
        <h2 className="text-2xl md:text-4xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
          What Our Customers Say
        </h2>
        <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
          Real reviews from real customers
        </p>
      </div>

      {/* Main Review Card */}
      <div className="relative max-w-3xl mx-auto">
        <div className="rounded-3xl p-6 md:p-10 shadow-xl relative overflow-hidden"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

          {/* Decorative quote mark */}
          <div className="absolute top-4 left-6 text-8xl md:text-9xl font-serif opacity-10"
            style={{ color: 'var(--color-link)' }}>
            "
          </div>

          <div className="relative z-10 text-center">

            {/* Avatar */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold anim-popIn"
                style={{ backgroundColor: 'var(--color-accent)' }}>
                {review.customerName?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>

            {/* Stars */}
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20}
                  className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
              ))}
            </div>

            {/* Comment */}
            <p className="text-base md:text-xl italic leading-relaxed mb-6 anim-fadeIn"
              style={{ color: 'var(--color-primary-text)' }}>
              "{review.comment}"
            </p>

            {/* Name + Product */}
            <p className="font-bold text-base md:text-lg mb-1" style={{ color: 'var(--color-link)' }}>
              {review.customerName || 'Anonymous'}
            </p>
            {review.productTitle && (
              <p className="text-xs md:text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                📦 {review.productTitle}
              </p>
            )}
          </div>
        </div>

        {/* Prev Button */}
        {reviews.length > 1 && (
          <button onClick={prevReview}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shadow-lg btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
            <ChevronLeft size={20} />
          </button>
        )}

        {/* Next Button */}
        {reviews.length > 1 && (
          <button onClick={nextReview}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shadow-lg btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
            <ChevronRight size={20} />
          </button>
        )}

        {/* Dots */}
        {reviews.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, i) => (
              <button key={i} onClick={() => setCurrentIndex(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === currentIndex ? '24px' : '8px',
                  height: '8px',
                  backgroundColor: i === currentIndex ? 'var(--color-accent)' : 'var(--color-border)',
                }} />
            ))}
          </div>
        )}
      </div>

    </section>
  );
};

export default ReviewSlider;